import React, { useCallback, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import Checkbox from "../Checkbox"
import ServiceTags from "../ServiceTags"
import CustomersCell from "../CustomersCell"
import Pagination from "../Pagination"
import {
  BUNDLE_CARD_BORDER_L,
  BUNDLE_EASE,
  BUNDLE_ROW_BG,
  PAGE_SIZE,
} from "../../utils/variables/common"
import StatusBadge from "../StatusBadge"
import {
  getOrderId,
  getHotelName,
  getHotelRegion,
  getHotelCountry,
  getHotelRoom,
  getHotelBoard,
  getHotelCategory,
  getHotelNights,
  getFromDate,
  getToDate,
  getClientCost,
  getCurrency,
  getCustomers,
  hasInsurance,
  hasTransfer,
  hasTransport,
} from "../../utils/parsers/orders.js"
import { Trans, useTranslation } from "react-i18next"
import OverwriteConfirmModal from "../OverwriteConfirmModal/OverwriteConfirmModal.jsx"

const bannerVariants = {
  hidden: { opacity: 0, y: -6, scaleY: 0.95, transformOrigin: "top" },
  visible: {
    opacity: 1,
    y: 0,
    scaleY: 1,
    transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: -4,
    scaleY: 0.95,
    transition: { duration: 0.16, ease: "easeIn" },
  },
}

const rowVariants = {
  hidden: { opacity: 0 },
  show: (i) => ({
    opacity: 1,
    transition: { duration: 0.15, ease: "easeOut", delay: i * 0.02 },
  }),
}

const footerVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
  exit: { opacity: 0, y: 4, transition: { duration: 0.14 } },
}

const badgeVariants = {
  hidden: { opacity: 0, scale: 0.8, y: -3 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 420, damping: 24 },
  },
  exit: { opacity: 0, scale: 0.8, y: 3, transition: { duration: 0.12 } },
}

const ImportStep = ({
  orders,
  importedIds,
  onImport,
  isImporting,
  isFetching,
  onBack,
  error,
  onOpenCustomers,
}) => {
  const { t } = useTranslation()

  const [page, setPage] = useState(1)
  const [showDuplicates, setShowDuplicates] = useState(false)
  const [confirmModal, setConfirmModal] = useState(false)

  const [selectedForImport, setSelectedForImport] = useState(
    () => new Set(orders.filter((o) => !o.already_imported).map((o) => getOrderId(o)))
  )

  const prevOrdersRef = React.useRef(orders)
  if (prevOrdersRef.current !== orders) {
    prevOrdersRef.current = orders
    setSelectedForImport(
      new Set(orders.filter((o) => !o.already_imported).map((o) => getOrderId(o)))
    )
  }

  const duplicatesCount = useMemo(() => orders.filter((o) => o.already_imported).length, [orders])

  const visibleOrders = useMemo(
    () => (showDuplicates ? orders : orders.filter((o) => !o.already_imported)),
    [orders, showDuplicates]
  )

  const toggleOrder = useCallback((id) => {
    setSelectedForImport((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    if (selectedForImport.size === visibleOrders.length) {
      setSelectedForImport(new Set())
    } else {
      setSelectedForImport(new Set(visibleOrders.map((o) => getOrderId(o))))
    }
  }, [selectedForImport.size, visibleOrders])

  const selectedOrders = useMemo(
    () => orders.filter((o) => selectedForImport.has(getOrderId(o))),
    [orders, selectedForImport]
  )

  const paginated = useMemo(
    () => visibleOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visibleOrders, page]
  )

  const allDone =
    importedIds.size > 0 &&
    selectedOrders.every((o) => importedIds.has(getOrderId(o))) &&
    selectedForImport.size > 0

  const selectedTotal = useMemo(
    () => selectedOrders.reduce((sum, o) => sum + Number(getClientCost(o) || 0), 0),
    [selectedOrders]
  )

  const COLS = "32px 40px 96px minmax(0, 1fr) 140px 100px 100px 110px"

  // хендлер кнопки импорта
  const handleImportClick = () => {
    if (selectedForImport.size === 0) return
    const hasDuplicates = selectedOrders.some((o) => o.already_imported)
    hasDuplicates ? setConfirmModal(true) : onImport(selectedOrders)
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 32 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 32 }}
      transition={{ duration: 0.3, ease: BUNDLE_EASE }}
      className="flex-1 flex flex-col"
    >
      {/* Sub-header */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-5 py-2.5 flex items-center gap-3 flex-wrap">
        <motion.button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors shrink-0"
          whileHover={{ x: -2 }}
          whileTap={{ scale: 0.95 }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t("admin.back")}
        </motion.button>
        <div className="w-px h-4 bg-gray-200 shrink-0" />

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">{t("sync.selected_for_import")}:</span>
          <AnimatePresence mode="wait">
            <motion.span
              key={selectedForImport.size}
              variants={badgeVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${
                selectedForImport.size === 0
                  ? "bg-red-50 border-red-200 text-red-600"
                  : selectedForImport.size === orders.length
                    ? "bg-blue-50 border-blue-200 text-blue-700"
                    : "bg-amber-50 border-amber-200 text-amber-700"
              }`}
            >
              {selectedForImport.size} / {orders.length}
            </motion.span>
          </AnimatePresence>

          {selectedForImport.size > 0 && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs text-gray-400 hidden sm:inline"
            >
              · {selectedTotal.toFixed(2)} EUR
            </motion.span>
          )}

          {selectedForImport.size < orders.length && selectedForImport.size > 0 && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => setSelectedForImport(new Set(orders.map((o) => getOrderId(o))))}
              className="text-xs text-blue-500 hover:text-blue-700 font-medium transition-colors"
            >
              {t("admin.select_all")}
            </motion.button>
          )}

          <AnimatePresence>
            {duplicatesCount > 0 && (
              <motion.button
                variants={bannerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => setShowDuplicates((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors cursor-pointer select-none ${
                  showDuplicates
                    ? "bg-amber-100 border-amber-400 text-amber-800"
                    : "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                }`}
              >
                <motion.span
                  animate={{ rotate: showDuplicates ? 90 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="inline-block"
                >
                  ⚠
                </motion.span>
                <Trans
                  i18nKey="sync.duplicates_count"
                  count={duplicatesCount}
                  values={{ count: duplicatesCount }}
                />
                <motion.svg
                  className="w-3 h-3 ml-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  animate={{ rotate: showDuplicates ? 180 : 0 }}
                  transition={{ duration: 0.22 }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </motion.svg>
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <AnimatePresence mode="wait">
            {allDone ? (
              <motion.span
                key="done"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{
                  scale: 1,
                  opacity: 1,
                  transition: { type: "spring", stiffness: 380, damping: 22 },
                }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-xl font-bold"
              >
                ✓ {t("sync.imported")}
              </motion.span>
            ) : (
              <motion.button
                key="btn"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={handleImportClick}
                disabled={isImporting || selectedForImport.size === 0 || isFetching}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                  isImporting || selectedForImport.size === 0 || isFetching
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                }`}
                whileHover={
                  !isImporting && selectedForImport.size > 0 && !isFetching ? { scale: 1.03 } : {}
                }
                whileTap={
                  !isImporting && selectedForImport.size > 0 && !isFetching ? { scale: 0.96 } : {}
                }
              >
                {isImporting ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    {t("sync.process")}...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      />
                    </svg>
                    <Trans
                      i18nKey="sync.import_applications"
                      values={{ count: selectedForImport.size }}
                    />
                  </>
                )}
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="px-4 md:px-5 flex flex-col gap-2 overflow-hidden">
        <AnimatePresence>
          {selectedForImport.size === 0 && !isFetching && (
            <motion.div
              variants={bannerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="mt-3 flex items-center gap-2 px-4 py-3 bg-red-50 rounded-xl border border-red-200"
            >
              <svg
                className="w-4 h-4 text-red-400 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
              <p className="text-xs text-red-600 font-medium">
                {t("sync.select_least_one_application")}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {duplicatesCount > 0 && !showDuplicates && !isFetching && (
            <motion.div
              variants={bannerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="mt-3 flex items-center justify-between gap-2 px-4 py-3 bg-amber-50 rounded-xl border border-amber-200"
            >
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-amber-500 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  />
                </svg>
                <p className="text-xs text-amber-700 font-medium">
                  <Trans
                    i18nKey="sync.existing_hidden_applications"
                    count={duplicatesCount}
                    values={{ count: duplicatesCount }}
                  />
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => setShowDuplicates(true)}
                className="text-xs text-amber-700 font-semibold underline underline-offset-2 hover:text-amber-900 shrink-0"
              >
                {t("sync.show")}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence mode="wait">
        {isFetching ? (
          <motion.div
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 flex flex-col items-center justify-center gap-4 py-24 text-gray-400"
          >
            <motion.div
              className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center"
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            >
              <svg className="w-6 h-6 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            </motion.div>
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-600">
                {t("sync.downloading_applications")}...
              </p>
              <p className="text-xs text-gray-400 mt-1">{t("sync.receiving_data_from_provider")}</p>
            </div>
            <div className="w-full max-w-2xl px-4 md:px-5 mt-2 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="h-16 bg-gray-100 rounded-xl"
                  animate={{ opacity: [0.4, 0.8, 0.4] }}
                  transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.15 }}
                />
              ))}
            </div>
          </motion.div>
        ) : error ? (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="flex-1 flex flex-col items-center justify-center gap-4 py-24"
          >
            <motion.div
              className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 340, damping: 22 }}
            >
              <svg
                className="w-7 h-7 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
            </motion.div>
            <div className="text-center max-w-xs">
              <p className="text-sm font-semibold text-gray-700">{t("sync.server_error")}</p>
              <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">
                {typeof error === "string" ? error : t("sync.try_again_later")}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={onBack}
              className="mt-1 flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {t("admin.back")}
            </motion.button>
          </motion.div>
        ) : orders.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="flex-1 flex flex-col items-center justify-center gap-4 py-24"
          >
            <motion.div
              className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 340, damping: 22 }}
            >
              <svg
                className="w-7 h-7 text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </motion.div>
            <div className="text-center max-w-xs">
              <p className="text-sm font-semibold text-gray-600">
                {t("sync.no_applications_found")}
              </p>
              <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">
                {t("sync.no_new_applications")}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={onBack}
              className="mt-1 flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {t("admin.back")}
            </motion.button>
          </motion.div>
        ) : (
          <motion.div
            key="table"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="px-0 md:px-5 py-4 flex-1"
          >
            <div className="bg-white md:border md:border-gray-200 md:rounded-lg md:shadow-sm">
              {/* Desktop head */}
              <div
                className="hidden md:grid text-[11px] font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-200"
                style={{ gridTemplateColumns: COLS }}
              >
                <div className="py-2.5 px-3 flex items-center justify-center">
                  <Checkbox
                    checked={
                      selectedForImport.size === visibleOrders.length && visibleOrders.length > 0
                    }
                    indeterminate={
                      selectedForImport.size > 0 && selectedForImport.size < visibleOrders.length
                    }
                    onChange={toggleAll}
                  />
                </div>
                <div className="px-3 py-2.5">№</div>
                {[
                  "ID",
                  t("table.hotel_room_column"),
                  t("table.tourists_column"),
                  t("table.checkin_column"),
                  t("table.checkout_column"),
                  t("table.amount_column"),
                ].map((h, i) => (
                  <div key={i} className={`px-3 py-2.5 ${i === 5 ? "text-right" : ""}`}>
                    {h}
                  </div>
                ))}
              </div>

              {/* Mobile head */}
              <div className="md:hidden bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center gap-3">
                <Checkbox
                  checked={
                    selectedForImport.size === visibleOrders.length && visibleOrders.length > 0
                  }
                  indeterminate={
                    selectedForImport.size > 0 && selectedForImport.size < visibleOrders.length
                  }
                  onChange={toggleAll}
                />
                <span className="text-xs text-gray-500">
                  {selectedForImport.size > 0
                    ? `${selectedForImport.size} обрано з ${visibleOrders.length}`
                    : `Обрати всі (${visibleOrders.length})`}
                </span>
                {selectedForImport.size > 0 && (
                  <span className="text-xs text-gray-400 ml-auto">{selectedTotal.toFixed(2)}</span>
                )}
              </div>

              <div className="divide-y divide-gray-100">
                {paginated.map((order, idx) => {
                  const orderId = getOrderId(order)
                  const done = importedIds.has(orderId)
                  const isSelected = selectedForImport.has(orderId)
                  const isDuplicate = order.already_imported

                  const rowBg = done
                    ? "bg-emerald-50"
                    : isDuplicate && !isSelected
                      ? "bg-amber-50"
                      : isSelected
                        ? BUNDLE_ROW_BG[order.status] || "bg-white"
                        : "bg-gray-50 opacity-60"

                  const borderLeft = done
                    ? "border-l-emerald-400"
                    : isDuplicate
                      ? "border-l-amber-400"
                      : isSelected
                        ? BUNDLE_CARD_BORDER_L[order.status] || "border-l-gray-300"
                        : "border-l-gray-200"

                  return (
                    <motion.div
                      key={orderId}
                      custom={idx}
                      variants={rowVariants}
                      initial="hidden"
                      animate="show"
                    >
                      {/* Mobile row */}
                      <div className={`md:hidden border-b border-gray-100 transition-all ${rowBg}`}>
                        <div className={`border-l-4 ${borderLeft} px-4 py-3`}>
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={isSelected}
                              indeterminate={false}
                              onChange={() => toggleOrder(orderId)}
                              className="mt-0.5 shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-mono font-bold text-blue-600 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded text-xs">
                                    #{orderId}
                                  </span>
                                  <StatusBadge status={order.status} />
                                  {isDuplicate && (
                                    <motion.span
                                      initial={{ opacity: 0, scale: 0.8 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 border border-amber-300 text-amber-700 text-[10px] font-bold rounded"
                                    >
                                      ⚠ {t("admin.duplicates")}
                                    </motion.span>
                                  )}
                                  {done && (
                                    <motion.span
                                      initial={{ opacity: 0, scale: 0.8 }}
                                      animate={{
                                        opacity: 1,
                                        scale: 1,
                                        transition: {
                                          type: "spring",
                                          stiffness: 400,
                                          damping: 20,
                                        },
                                      }}
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-600 text-white text-[10px] font-bold rounded"
                                    >
                                      ✓ {t("sync.import_short")}
                                    </motion.span>
                                  )}
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-sm tabular-nums">
                                    {getClientCost(order)}
                                  </div>
                                  <div className="text-[10px] text-gray-400">
                                    {getCurrency(order)}
                                  </div>
                                </div>
                              </div>
                              <div className="font-semibold text-sm text-gray-800">
                                {getHotelName(order)}
                              </div>
                              <div className="text-xs text-gray-400">
                                {getHotelRoom(order)} · {getFromDate(order)} — {getToDate(order)}
                              </div>
                              <div className="flex items-center gap-3 mt-1.5">
                                <CustomersCell
                                  customers={getCustomers(order)}
                                  onOpen={onOpenCustomers}
                                />
                                <ServiceTags
                                  insurance={hasInsurance(order)}
                                  transfer={hasTransfer(order)}
                                  transport={hasTransport(order)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Desktop row */}
                      <motion.div
                        className={`hidden md:grid items-start text-xs transition-colors duration-200 ${rowBg} hover:brightness-[0.98]`}
                        style={{ gridTemplateColumns: COLS }}
                        whileHover={{ backgroundColor: "rgba(0,0,0,0.012)" }}
                      >
                        <div className="px-3 py-3 flex items-start justify-center pt-3.5">
                          <Checkbox
                            checked={isSelected}
                            indeterminate={false}
                            onChange={() => toggleOrder(orderId)}
                          />
                        </div>
                        <div className="px-3 py-3 text-gray-400 font-mono pt-3.5">
                          {(page - 1) * PAGE_SIZE + idx + 1}
                        </div>
                        <div className="px-2 py-3">
                          <div className="font-mono font-bold text-blue-600 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded inline-block mb-1">
                            #{orderId}
                          </div>
                          <div>
                            <StatusBadge status={order.status} />
                          </div>
                          {isDuplicate && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.85 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="mt-1"
                            >
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 border border-amber-300 text-amber-700 text-[10px] font-bold rounded">
                                ⚠ {t("admin.duplicates")}
                              </span>
                            </motion.div>
                          )}
                          {done && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.85 }}
                              animate={{
                                opacity: 1,
                                scale: 1,
                                transition: { type: "spring", stiffness: 400, damping: 20 },
                              }}
                              className="mt-1"
                            >
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-600 text-white text-[10px] font-bold rounded">
                                ✓
                              </span>
                            </motion.div>
                          )}
                        </div>
                        <div className="px-3 py-3 min-w-0 overflow-hidden">
                          <div className="font-semibold text-gray-800 leading-snug truncate">
                            {getHotelName(order)}
                          </div>
                          <div className="text-gray-400 mt-0.5 truncate">
                            {getHotelRoom(order)} · {getHotelBoard(order)} ·{" "}
                            <span className="font-medium text-gray-600">
                              {getHotelCategory(order)}
                            </span>
                          </div>
                          <div className="text-gray-400 mt-0.5 truncate">
                            {getHotelRegion(order)}, {getHotelCountry(order)} ·{" "}
                            <span className="font-medium text-gray-600">
                              {getHotelNights(order)} {t("admin.nights_short")}
                            </span>
                          </div>
                          <div className="mt-1">
                            <ServiceTags
                              insurance={hasInsurance(order)}
                              transfer={hasTransfer(order)}
                              transport={hasTransport(order)}
                            />
                          </div>
                        </div>
                        <div className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <CustomersCell customers={getCustomers(order)} onOpen={onOpenCustomers} />
                        </div>
                        <div className="px-3 py-3 font-medium text-gray-700">
                          {getFromDate(order)}
                        </div>
                        <div className="px-3 py-3 font-medium text-gray-700">
                          {getToDate(order)}
                        </div>
                        <div className="px-3 py-3 text-right">
                          <div
                            className={`font-bold tabular-nums ${isSelected ? "text-gray-800" : "text-gray-400"}`}
                          >
                            {getClientCost(order)}
                          </div>
                          <div className="text-gray-400">{getCurrency(order)}</div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )
                })}
              </div>

              <AnimatePresence>
                {selectedForImport.size > 0 && (
                  <motion.div
                    variants={footerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between"
                  >
                    <span className="text-xs text-gray-500 font-medium">
                      {t("sync.total_amount_applications")}
                    </span>
                    <motion.span
                      key={selectedTotal}
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.18 }}
                      className="text-sm font-bold text-gray-800 tabular-nums"
                    >
                      {selectedTotal.toFixed(2)}
                    </motion.span>
                  </motion.div>
                )}
              </AnimatePresence>

              <Pagination
                total={visibleOrders.length}
                page={page}
                pageSize={PAGE_SIZE}
                onChange={(p) => {
                  setPage(p)
                  window.scrollTo({ top: 0, behavior: "smooth" })
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <OverwriteConfirmModal
        isOpen={confirmModal}
        orders={selectedOrders}
        onClose={() => setConfirmModal(false)}
        onConfirm={() => {
          setConfirmModal(false)
          onImport(selectedOrders)
        }}
      />
    </motion.div>
  )
}

export default ImportStep
